import { getLs, setLs } from "./utils";

export const VIDEO_PROCESSING_ADVANCED_TOGGLE_LS_KEY = "videoProcessingShowAdvancedOptions";
export const VIDEO_PROCESSING_OPTIONS_LS_KEY = "videoProcessingLastOptions";
export const VIDEO_OUTPUT_EXTENSION = "mp4";
export const VIDEO_MASTER_FILE_SUFFIX = "-master";
export const VIDEO_ORIENTATION_PORTRAIT = "portrait";
export const VIDEO_ORIENTATION_LANDSCAPE = "landscape";
export const VIDEO_ORIENTATION_SQUARE = "square";
export const VIDEO_ORIENTATION_MIXED = "mixed";
export const VIDEO_ORIENTATION_UNKNOWN = "unknown";

export const VIDEO_TONE_MAP_PRESET_OPTIONS = [
	{ value: "off", label: "Do not tone-map HDR" },
	{ value: "auto-hable", label: "Automatic HDR to SDR (Hable)" },
	{ value: "auto-mobius", label: "Automatic HDR to SDR (Mobius)" },
	{ value: "auto-reinhard", label: "Automatic HDR to SDR (Reinhard)" },
	{ value: "auto-clip", label: "Automatic HDR to SDR (Clip)" }
];

export const VIDEO_STYLE_FILTER_PRESET_OPTIONS = [
	{ value: "none", label: "No style filter" },
	{ value: "black-white", label: "Black & White" },
	{ value: "sepia", label: "Sepia" },
	{ value: "high-contrast", label: "High Contrast" },
	{ value: "vivid", label: "Vivid Color" }
];

export const VIDEO_RESIZE_PRESET_OPTIONS = [
	{ value: "source", label: "Keep source dimensions" },
	{ value: "fit-1920x1080", label: "Fit within 1920x1080" },
	{ value: "fit-1280x720", label: "Fit within 1280x720" },
	{ value: "fit-1080x1920", label: "Fit within 1080x1920" }
];

export const VIDEO_FRAMING_SOURCE_PRESET_OPTION = { value: "source", label: "Keep source framing" };
export const VIDEO_FRAMING_LANDSCAPE_PRESET_OPTIONS = [
	{ value: "blur-fill-1280x720", label: "Blur-fill to 1280x720 landscape" },
	{ value: "blur-fill-1920x1080", label: "Blur-fill to 1920x1080 landscape" },
	{ value: "crop-fill-1280x720", label: "Center crop to 1280x720 landscape" },
	{ value: "crop-fill-1920x1080", label: "Center crop to 1920x1080 landscape" },
	{ value: "pad-black-1280x720", label: "Pad to 1280x720 landscape (black bars)" },
	{ value: "pad-black-1920x1080", label: "Pad to 1920x1080 landscape (black bars)" }
];

export const VIDEO_FRAMING_PORTRAIT_PRESET_OPTIONS = [
	{ value: "blur-fill-720x1280", label: "Blur-fill to 720x1280 portrait" },
	{ value: "blur-fill-1080x1920", label: "Blur-fill to 1080x1920 portrait" },
	{ value: "crop-fill-720x1280", label: "Center crop to 720x1280 portrait" },
	{ value: "crop-fill-1080x1920", label: "Center crop to 1080x1920 portrait" },
	{ value: "pad-black-720x1280", label: "Pad to 720x1280 portrait (black bars)" },
	{ value: "pad-black-1080x1920", label: "Pad to 1080x1920 portrait (black bars)" }
];

export const VIDEO_FRAMING_PRESET_OPTIONS = [
	VIDEO_FRAMING_SOURCE_PRESET_OPTION,
	...VIDEO_FRAMING_LANDSCAPE_PRESET_OPTIONS,
	...VIDEO_FRAMING_PORTRAIT_PRESET_OPTIONS
];

export const VIDEO_ROTATION_PRESET_OPTIONS = [
	{ value: "source", label: "Keep source orientation" },
	{ value: "rotate-90-cw", label: "Rotate 90° clockwise" },
	{ value: "rotate-90-ccw", label: "Rotate 90° counterclockwise" },
	{ value: "rotate-180", label: "Rotate 180°" },
	{ value: "flip-horizontal", label: "Flip horizontally" },
	{ value: "flip-vertical", label: "Flip vertically" }
];

export const VIDEO_AUDIO_PRESET_OPTIONS = [
	{ value: "keep", label: "Keep source audio" },
	{ value: "mute", label: "Mute output audio" }
];

export const DEFAULT_VIDEO_PROCESSING_OPTIONS = Object.freeze({
	hdrToneMapPreset: "off",
	styleFilterPreset: "none",
	resizePreset: "source",
	framingPreset: "source",
	rotationPreset: "source",
	audioPreset: "keep",
	trimStartSeconds: 0,
	trimDurationSeconds: 0,
	thumbnailFrameSeconds: 0,
	createThumbnailArtifact: false,
	autoLinkThumbnailArtifact: true
});

const VIDEO_FILE_EXTENSION_PATTERN = /\.(mp4|m4v|mov|webm|ogv|ogg|mkv|avi|wmv|mxf)$/i;
const VIDEO_MIME_TYPE_EXTENSION_MAP = {
	"video/mp4": "mp4",
	"video/quicktime": "mov",
	"video/x-m4v": "m4v",
	"video/webm": "webm",
	"video/ogg": "ogv",
	"video/x-matroska": "mkv",
	"video/x-msvideo": "avi",
	"video/x-ms-wmv": "wmv",
	"application/mxf": "mxf"
};

function normalizeNonNegativeSeconds(value = 0) {
	const parsed = Number.parseFloat(`${value}`);
	if (!Number.isFinite(parsed) || parsed <= 0) {
		return 0;
	}
	return Math.round(parsed * 100) / 100;
}

export function normalizeVideoProcessingOptions(raw = {}) {
	const source = raw && typeof raw === "object" ? raw : {};
	const normalized = {
		hdrToneMapPreset: `${source.hdrToneMapPreset || DEFAULT_VIDEO_PROCESSING_OPTIONS.hdrToneMapPreset}`.trim(),
		styleFilterPreset: `${source.styleFilterPreset || DEFAULT_VIDEO_PROCESSING_OPTIONS.styleFilterPreset}`.trim(),
		resizePreset: `${source.resizePreset || DEFAULT_VIDEO_PROCESSING_OPTIONS.resizePreset}`.trim(),
		framingPreset: `${source.framingPreset || DEFAULT_VIDEO_PROCESSING_OPTIONS.framingPreset}`.trim(),
		rotationPreset: `${source.rotationPreset || DEFAULT_VIDEO_PROCESSING_OPTIONS.rotationPreset}`.trim(),
		audioPreset: `${source.audioPreset || DEFAULT_VIDEO_PROCESSING_OPTIONS.audioPreset}`.trim(),
		trimStartSeconds: normalizeNonNegativeSeconds(source.trimStartSeconds),
		trimDurationSeconds: normalizeNonNegativeSeconds(source.trimDurationSeconds),
		thumbnailFrameSeconds: normalizeNonNegativeSeconds(source.thumbnailFrameSeconds),
		createThumbnailArtifact: source.createThumbnailArtifact === true,
		autoLinkThumbnailArtifact: source.hasOwnProperty("autoLinkThumbnailArtifact")
			? source.autoLinkThumbnailArtifact === true
			: DEFAULT_VIDEO_PROCESSING_OPTIONS.autoLinkThumbnailArtifact === true
	};

	if (!VIDEO_TONE_MAP_PRESET_OPTIONS.some((option) => option.value === normalized.hdrToneMapPreset)) {
		normalized.hdrToneMapPreset = DEFAULT_VIDEO_PROCESSING_OPTIONS.hdrToneMapPreset;
	}
	if (!VIDEO_STYLE_FILTER_PRESET_OPTIONS.some((option) => option.value === normalized.styleFilterPreset)) {
		normalized.styleFilterPreset = DEFAULT_VIDEO_PROCESSING_OPTIONS.styleFilterPreset;
	}
	if (!VIDEO_RESIZE_PRESET_OPTIONS.some((option) => option.value === normalized.resizePreset)) {
		normalized.resizePreset = DEFAULT_VIDEO_PROCESSING_OPTIONS.resizePreset;
	}
	if (!VIDEO_FRAMING_PRESET_OPTIONS.some((option) => option.value === normalized.framingPreset)) {
		normalized.framingPreset = DEFAULT_VIDEO_PROCESSING_OPTIONS.framingPreset;
	}
	if (!VIDEO_ROTATION_PRESET_OPTIONS.some((option) => option.value === normalized.rotationPreset)) {
		normalized.rotationPreset = DEFAULT_VIDEO_PROCESSING_OPTIONS.rotationPreset;
	}
	if (!VIDEO_AUDIO_PRESET_OPTIONS.some((option) => option.value === normalized.audioPreset)) {
		normalized.audioPreset = DEFAULT_VIDEO_PROCESSING_OPTIONS.audioPreset;
	}

	return normalized;
}

export function getStoredVideoProcessingOptions() {
	return normalizeVideoProcessingOptions(getLs(VIDEO_PROCESSING_OPTIONS_LS_KEY) || DEFAULT_VIDEO_PROCESSING_OPTIONS);
}

export function setStoredVideoProcessingOptions(options = {}) {
	setLs(VIDEO_PROCESSING_OPTIONS_LS_KEY, normalizeVideoProcessingOptions(options));
}

export function isVideoAdvancedOptionsEnabled() {
	return getLs(VIDEO_PROCESSING_ADVANCED_TOGGLE_LS_KEY) === true;
}

export function setVideoAdvancedOptionsEnabled(enabled) {
	setLs(VIDEO_PROCESSING_ADVANCED_TOGGLE_LS_KEY, enabled === true);
}

export function isVideoFileCandidate(file = null, propName = "", accept = "") {
	const normalizedPropName = `${propName || ""}`.trim().toLowerCase();
	if (normalizedPropName === "video") {
		return true;
	}

	const normalizedAccept = `${accept || ""}`.trim().toLowerCase();
	if (normalizedAccept.includes("video/")) {
		return true;
	}

	const mimeType = `${file && file.type ? file.type : ""}`.trim().toLowerCase();
	if (mimeType.startsWith("video/")) {
		return true;
	}

	const fileName = `${file && file.name ? file.name : ""}`.trim();
	return VIDEO_FILE_EXTENSION_PATTERN.test(fileName);
}

export function shouldPromptForVideoProcessing({ file = null, propName = "", accept = "", showAdvanced = false, skipOptimization = false } = {}) {
	return !skipOptimization && showAdvanced === true && isVideoFileCandidate(file, propName, accept);
}

export function getDefaultVideoOrientationSummary(videoCount = 0) {
	return {
		orientation: VIDEO_ORIENTATION_UNKNOWN,
		videoCount: Number(videoCount) || 0,
		analyzed: false,
		items: []
	};
}

export function getVideoOrientationFromDimensions(width = 0, height = 0) {
	const normalizedWidth = Number(width) || 0;
	const normalizedHeight = Number(height) || 0;
	if (!(normalizedWidth > 0) || !(normalizedHeight > 0)) {
		return VIDEO_ORIENTATION_UNKNOWN;
	}
	if (normalizedHeight > normalizedWidth) {
		return VIDEO_ORIENTATION_PORTRAIT;
	}
	if (normalizedWidth > normalizedHeight) {
		return VIDEO_ORIENTATION_LANDSCAPE;
	}
	return VIDEO_ORIENTATION_SQUARE;
}

export function getSmartVideoFramingPresetOptions(orientationSummary = null) {
	const orientation = `${orientationSummary && orientationSummary.orientation ? orientationSummary.orientation : ""}`.trim().toLowerCase();
	switch (orientation) {
		case VIDEO_ORIENTATION_PORTRAIT:
			return [VIDEO_FRAMING_SOURCE_PRESET_OPTION, ...VIDEO_FRAMING_LANDSCAPE_PRESET_OPTIONS];
		case VIDEO_ORIENTATION_LANDSCAPE:
			return [VIDEO_FRAMING_SOURCE_PRESET_OPTION, ...VIDEO_FRAMING_PORTRAIT_PRESET_OPTIONS];
		default:
			return VIDEO_FRAMING_PRESET_OPTIONS;
	}
}

export async function probeVideoFileOrientation(file = null) {
	return new Promise((resolve) => {
		const fallback = {
			fileName: `${file && file.name ? file.name : ""}`.trim(),
			width: 0,
			height: 0,
			orientation: VIDEO_ORIENTATION_UNKNOWN
		};
		if (
			!file ||
			typeof document === "undefined" ||
			typeof URL === "undefined" ||
			typeof URL.createObjectURL !== "function"
		) {
			resolve(fallback);
			return;
		}

		const objectUrl = URL.createObjectURL(file);
		const video = document.createElement("video");
		let settled = false;
		let timeoutId = null;

		const finalize = (result = fallback) => {
			if (settled) {
				return;
			}
			settled = true;
			if (timeoutId) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}
			try {
				video.pause();
			} catch (err) {
				// ignore cleanup errors
			}
			try {
				video.removeAttribute("src");
				video.load();
			} catch (err) {
				// ignore cleanup errors
			}
			try {
				URL.revokeObjectURL(objectUrl);
			} catch (err) {
				// ignore cleanup errors
			}
			resolve(result);
		};

		video.preload = "metadata";
		video.muted = true;
		video.playsInline = true;
		video.onloadedmetadata = () => {
			const width = Number(video.videoWidth) || 0;
			const height = Number(video.videoHeight) || 0;
			finalize({
				fileName: fallback.fileName,
				width,
				height,
				orientation: getVideoOrientationFromDimensions(width, height)
			});
		};
		video.onerror = () => {
			finalize(fallback);
		};
		timeoutId = setTimeout(() => {
			finalize(fallback);
		}, 4000);
		video.src = objectUrl;
		try {
			video.load();
		} catch (err) {
			finalize(fallback);
		}
	});
}

export async function summarizeVideoFileOrientations(files = []) {
	const videoFiles = (Array.isArray(files) ? files : []).filter((file) => isVideoFileCandidate(file));
	if (videoFiles.length === 0) {
		return getDefaultVideoOrientationSummary(0);
	}

	const items = await Promise.all(videoFiles.map((file) => probeVideoFileOrientation(file)));
	const knownOrientations = Array.from(new Set(items.map((item) => item.orientation).filter((orientation) => {
		return orientation && orientation !== VIDEO_ORIENTATION_UNKNOWN;
	})));

	let orientation = VIDEO_ORIENTATION_UNKNOWN;
	if (knownOrientations.length === 1) {
		orientation = knownOrientations[0];
	} else if (knownOrientations.length > 1) {
		orientation = VIDEO_ORIENTATION_MIXED;
	}

	return {
		orientation,
		videoCount: videoFiles.length,
		analyzed: true,
		items
	};
}

export function buildDefaultVideoOutputFileName(fileName = "", extension = VIDEO_OUTPUT_EXTENSION) {
	const normalizedExtension = `${extension || VIDEO_OUTPUT_EXTENSION}`.replace(/^\./, "").trim().toLowerCase() || VIDEO_OUTPUT_EXTENSION;
	const trimmedFileName = `${fileName || ""}`.trim();
	const stem = trimmedFileName.replace(/\.[^/.]+$/, "").trim() || "video";
	return `${stem}.${normalizedExtension}`;
}

export function normalizeVideoOutputFileName(fileName = "", fallbackFileName = "", extension = VIDEO_OUTPUT_EXTENSION) {
	const sourceFileName = `${fileName || ""}`.trim() || `${fallbackFileName || ""}`.trim();
	return buildDefaultVideoOutputFileName(sourceFileName, extension);
}

function getVideoSourceExtension(sourceFileName = "", sourceMimeType = "", fallbackExtension = VIDEO_OUTPUT_EXTENSION) {
	const normalizedFileName = `${sourceFileName || ""}`.trim();
	if (normalizedFileName.includes(".")) {
		const parts = normalizedFileName.split(".");
		const extension = `${parts[parts.length - 1] || ""}`.trim().toLowerCase();
		if (extension) {
			return extension;
		}
	}

	const normalizedMimeType = `${sourceMimeType || ""}`.trim().toLowerCase();
	if (VIDEO_MIME_TYPE_EXTENSION_MAP[normalizedMimeType]) {
		return VIDEO_MIME_TYPE_EXTENSION_MAP[normalizedMimeType];
	}

	return `${fallbackExtension || VIDEO_OUTPUT_EXTENSION}`.replace(/^\./, "").trim().toLowerCase() || VIDEO_OUTPUT_EXTENSION;
}

export function buildVideoMasterFileName(outputFileName = "", sourceFileName = "", sourceMimeType = "", fallbackExtension = VIDEO_OUTPUT_EXTENSION) {
	const outputStem = `${outputFileName || ""}`.trim().replace(/\.[^/.]+$/, "").trim() || "video";
	const extension = getVideoSourceExtension(sourceFileName, sourceMimeType, fallbackExtension);
	return `${outputStem}${VIDEO_MASTER_FILE_SUFFIX}.${extension}`;
}

export function buildVideoMasterPath(videoPath = "", sourceFileName = "", sourceMimeType = "", fallbackExtension = VIDEO_OUTPUT_EXTENSION) {
	const normalizedPath = `${videoPath || ""}`.trim().replace(/^\/+/, "");
	if (!normalizedPath) {
		return "";
	}

	const lastSlashIndex = normalizedPath.lastIndexOf("/");
	const folderPath = lastSlashIndex >= 0 ? normalizedPath.substring(0, lastSlashIndex) : "";
	const outputFileName = lastSlashIndex >= 0 ? normalizedPath.substring(lastSlashIndex + 1) : normalizedPath;
	const masterFileName = buildVideoMasterFileName(outputFileName, sourceFileName, sourceMimeType, fallbackExtension);
	return `${folderPath ? `${folderPath}/` : ""}${masterFileName}`;
}

export function buildVideoProcessingDriveMetaData(options = {}) {
	return {
		videoProcessing: normalizeVideoProcessingOptions(options)
	};
}

export function encodeVideoProcessingOptionsForMetadata(options = {}) {
	return encodeURIComponent(JSON.stringify(normalizeVideoProcessingOptions(options)));
}

export function buildThumbnailArtifactPath(videoPath = "") {
	const normalizedPath = `${videoPath || ""}`.trim().replace(/^\/+/, "");
	if (!normalizedPath) {
		return "";
	}

	const lastSlashIndex = normalizedPath.lastIndexOf("/");
	const folderPath = lastSlashIndex >= 0 ? normalizedPath.substring(0, lastSlashIndex) : "";
	const fileName = lastSlashIndex >= 0 ? normalizedPath.substring(lastSlashIndex + 1) : normalizedPath;
	const stem = fileName.replace(/\.[^/.]+$/, "") || "video";

	return `${folderPath ? `${folderPath}/` : ""}${stem}-thumbnail.jpg`;
}
